/**
 * AI 문서 아카이브 — 공통 로컬 스토어 (Phase-1, mapper/BE 연결 전)
 * 설계: docs/agent-plans/CM/20260923-AI문서아카이브-OCR전자서명계약인사문서-통합설계분석.md §3
 *
 * 3층 구조 중 ①②를 이 파일이 담당한다.
 * ① 공통 "문서 인스턴스" 레지스트리 — docType 무관하게 원본참조+추출 JSON만 얇게 저장 (cm_doc_instance 가칭)
 * ② docType별 추출 스키마 — DOC_TYPES 레지스트리의 row (테이블 아님)
 * ③ 매핑 어댑터(업무테이블 반영)는 이 파일이 하지 않는다 — BE 연결 시 화면/서비스 쪽에서 담당.
 *
 * 키: erp.archive.docInstance|{tenant} — 세무법인/기관(tenant) 단위로 원천 분리.
 * 실제 OCR API 호출은 아직 없다. simulateExtraction()은 BE 연동 전 임시 목업이며,
 * 실제 연동 시 이 함수 하나만 서버 호출로 교체하면 된다(화면 코드는 변경 없음).
 */

const PREFIX = 'erp.archive.docInstance';

export const INPUT_METHODS = [
  { value: 'CAPTURE', label: '촬영' },
  { value: 'UPLOAD', label: '업로드' },
];

export const DOC_STATUS = [
  { value: 'PENDING', label: '검토대기' },
  { value: 'CONFIRMED', label: '확정' },
];

/**
 * docType 스키마 레지스트리 (§3 ②) — 새 문서 종류가 늘어도 테이블이 아니라 이 배열에 row만 추가한다.
 * category: 아카이브 허브 카드(§4) 단위 — OCR / CONTRACT / HR / SIGNATURE
 */
export const DOC_TYPES = [
  {
    value: 'CARD_RECEIPT',
    label: '카드영수증',
    category: 'OCR',
    sensitive: false,
    fields: [
      { field: 'merchantName', label: '가맹점' },
      { field: 'txDate', label: '거래일자' },
      { field: 'itemSummary', label: '품목요약' },
      { field: 'amount', label: '결제금액' },
    ],
  },
  {
    value: 'TAX_INVOICE',
    label: '세금계산서',
    category: 'OCR',
    sensitive: false,
    fields: [
      { field: 'supplierName', label: '공급자' },
      { field: 'supplierBizNo', label: '공급자 사업자번호' },
      { field: 'issueDate', label: '작성일자' },
      { field: 'supplyAmount', label: '공급가액' },
      { field: 'vatAmount', label: '세액' },
    ],
  },
  {
    value: 'CONTRACT',
    label: '계약서',
    category: 'CONTRACT',
    sensitive: false,
    fields: [
      { field: 'contractTitle', label: '계약명' },
      { field: 'counterpartyName', label: '상대방(업체명)' },
      { field: 'counterpartyBizNo', label: '상대방 사업자번호' },
      { field: 'contractAmount', label: '계약금액' },
      { field: 'contractPeriod', label: '계약기간' },
    ],
  },
  {
    value: 'BANK_BOOK',
    label: '통장사본',
    category: 'CONTRACT',
    sensitive: true,
    fields: [
      { field: 'bankName', label: '은행명' },
      { field: 'accountNo', label: '계좌번호' },
      { field: 'accountHolder', label: '예금주' },
    ],
  },
  {
    value: 'BIZ_REG',
    label: '사업자등록증',
    category: 'CONTRACT',
    sensitive: false,
    fields: [
      { field: 'corpName', label: '상호' },
      { field: 'bizNo', label: '사업자번호' },
      { field: 'ceoName', label: '대표자' },
      { field: 'bizType', label: '업태' },
      { field: 'bizItem', label: '종목' },
    ],
  },
  {
    value: 'HR_CENSUS',
    label: '등본',
    category: 'HR',
    sensitive: true,
    fields: [
      { field: 'residentName', label: '성명' },
      { field: 'rrn', label: '주민등록번호' },
      { field: 'address', label: '주소' },
    ],
  },
  {
    value: 'HR_FAMILY_CERT',
    label: '가족관계증명서',
    category: 'HR',
    sensitive: true,
    fields: [
      { field: 'residentName', label: '성명' },
      { field: 'rrn', label: '주민등록번호' },
      { field: 'familyMembers', label: '가족관계' },
    ],
  },
  {
    value: 'HR_LABOR_CONTRACT',
    label: '근로계약서',
    category: 'HR',
    sensitive: true,
    fields: [
      { field: 'empName', label: '성명' },
      { field: 'hireDate', label: '입사일' },
      { field: 'position', label: '직급' },
      { field: 'salary', label: '급여' },
    ],
  },
  {
    value: 'HR_CERT',
    label: '자격증·졸업증명서',
    category: 'HR',
    sensitive: false,
    fields: [
      { field: 'docTitle', label: '서류명' },
      { field: 'holderName', label: '성명' },
      { field: 'issuer', label: '발급기관' },
      { field: 'issueDate', label: '발급일' },
    ],
  },
];

export const docTypesByCategory = (category) => DOC_TYPES.filter((d) => d.category === category);
export const docTypeOf = (value) => DOC_TYPES.find((d) => d.value === value) || null;

export function createDocUid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `DOC-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function storageKey(tenantCode) {
  return `${PREFIX}|${tenantCode || ''}`;
}

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

/**
 * ① 공통 레지스트리 CRUD — tenant 스코프 전체를 담고, 화면에서 docType/category로 필터링한다.
 * 문서 종류가 늘어도 이 함수들은 그대로다(§3.2 — 화면 내장 OCR이든 아카이브 조회든 같은 저장소).
 */
export function loadDocInstances(tenantCode) {
  return readJson(storageKey(tenantCode), []);
}

export function loadDocInstancesByCategory(tenantCode, category) {
  return loadDocInstances(tenantCode).filter((d) => docTypeOf(d.docType)?.category === category);
}

export function saveDocInstance(tenantCode, doc) {
  const list = loadDocInstances(tenantCode);
  const idx = list.findIndex((d) => d.docUid === doc.docUid);
  const next = idx >= 0 ? [...list.slice(0, idx), doc, ...list.slice(idx + 1)] : [doc, ...list];
  writeJson(storageKey(tenantCode), next);
  return next;
}

export function removeDocInstance(tenantCode, docUid) {
  const next = loadDocInstances(tenantCode).filter((d) => d.docUid !== docUid);
  writeJson(storageKey(tenantCode), next);
  return next;
}

/**
 * 이미지 리사이즈 — 촬영/업로드 두 입력 경로 모두 이 함수를 거친다.
 * 한쪽만 거치면 절감 효과가 없다는 점을 [[20260923-OCR-LLM비전API-토큰소비및관리전략]] §7에서 확인했다.
 * 반환: { dataUrl, width, height }
 */
export function resizeImageFile(file, maxDim = 1280) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('파일을 읽지 못했습니다.'));
    reader.onload = () => {
      img.onerror = () => reject(new Error('이미지를 열지 못했습니다.'));
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const width = Math.max(1, Math.round(img.width * scale));
        const height = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve({ dataUrl: canvas.toDataURL('image/jpeg', 0.85), width, height });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

/** (가로×세로)÷750 근사식 — [[20260923-OCR-LLM비전API-토큰소비및관리전략]] §7.2 */
export function estimateImageTokens(width, height) {
  return Math.round((Number(width || 0) * Number(height || 0)) / 750);
}

/**
 * OCR/AI 추출 목업 — BE·API 키 연동 전 스텁.
 * docType 스키마의 필드에 더미값을 채워 "제안"하고, 화면에서 사람이 확인·수정 후 저장한다
 * (기존 원천세 AI매핑·샵관리 영수증 OCR과 동일한 "제안 → 확인 → 확정" 패턴).
 */
export function simulateExtraction(docType, { width, height, fileName } = {}) {
  const type = docTypeOf(docType);
  const delay = 700 + Math.round(Math.random() * 500);
  return new Promise((resolve) => {
    setTimeout(() => {
      const fields = {};
      (type?.fields || []).forEach((f) => {
        fields[f.field] = '';
      });
      resolve({
        fields,
        confidence: 0,
        tokenEstimate: estimateImageTokens(width, height),
        sourceFile: fileName || '',
        note: '자동 인식 결과가 아직 없습니다 — API 연동 전이라 값을 직접 입력·확인하세요.',
      });
    }, delay);
  });
}

export function formatAmt(v) {
  const n = Number(String(v ?? '').replace(/[,원]/g, ''));
  return Number.isFinite(n) ? n.toLocaleString('ko-KR') : String(v ?? '');
}

/** 주민등록번호류 마스킹 — 인사정보 카테고리(§4.4 최고 민감도) 미리보기에 사용 */
export function maskRrn(v) {
  const digits = String(v || '').replace(/\D/g, '');
  if (digits.length < 7) return v || '';
  return `${digits.slice(0, 6)}-${digits.slice(6, 7)}******`;
}
